import { WorkflowDurationUnit } from '~/client/graph/types.generated'
import type {
  INoteLine,
  IWarningNote,
} from '~/client/src/shared/components/WarningNote/WarningNote'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'

export interface IBookingInterval {
  startDate: number
  endDate: number
}

export type HoursProblem =
  | { kind: 'closedDay'; day: number; isWeekly: boolean }
  | { kind: 'startsEarly'; start: number; opens: number }
  | { kind: 'endsLate'; end: number; closes: number }
  | { kind: 'outsideBothEnds'; opens: number; closes: number }
  | { kind: 'closedThen'; opens: number; closes: number }
  | { kind: 'overnight'; closes: number; reopens?: number }
  | { kind: 'break' }

export interface IConflictingBooking extends IBookingInterval {
  code: string | null
}

export interface IOverlapConflict {
  // Parsed from the server's message; null when it gives no limit.
  limit: number | null
  bookings: IConflictingBooking[]
  failingCount: number
  totalCount: number
  firstFailingStart: number
}

export interface IClosureSpan {
  startDate: number
  endDate: number
  isDaily?: boolean
}

export interface IExceededMaxDuration {
  limit: { duration: number; unit: WorkflowDurationUnit }
  bookingMs: number
}

export interface IHoursOfOperationWarning {
  locationId: string
  resourceName: string
  isProjectHours: boolean
  // The warning is about the project's own hours (the date field), not a
  // resource inheriting them.
  isProject?: boolean
  hoursLabels: string[]
  problem: HoursProblem
  failingCount: number
  totalCount: number
  firstFailingStart: number
}

export function explainOccurrence(
  projectDateStore: ProjectDateStore,
  occurrence: IBookingInterval,
  allowedWindows: IMsDateInterval[],
  isAllDay: boolean,
): HoursProblem | null {
  const { startOfDay, addDays } = projectDateStore
  const concrete = projectDateStore
    .buildConcreteOperatingWindows(
      occurrence.startDate,
      occurrence.endDate,
      allowedWindows,
    )
    .sort((a, b) => a.startDate - b.startDate)
  const isWeekly = allowedWindows.some(w => w.dayOfWeek != null)

  const firstDay = startOfDay(occurrence.startDate).getTime()
  const lastDay = startOfDay(occurrence.endDate - 1).getTime()
  const days: number[] = []
  for (
    let cursor = firstDay;
    cursor <= lastDay;
    cursor = addDays(cursor, 1).getTime()
  ) {
    days.push(cursor)
  }

  const closedDay = days.find(day => {
    const dayEnd = addDays(day, 1).getTime()
    return !concrete.some(w => w.startDate < dayEnd && w.endDate > day)
  })
  if (closedDay !== undefined) {
    return { kind: 'closedDay', day: closedDay, isWeekly }
  }

  // All-day bookings name a day, not a time, so they are judged by the day only:
  // they conflict only when the resource works no hours at all on a day they cover.
  if (isAllDay) return null

  if (days.length > 1) {
    const current =
      concrete.find(
        w =>
          w.startDate <= occurrence.startDate &&
          w.endDate > occurrence.startDate,
      ) ?? concrete[0]
    const next = concrete.find(w => w.startDate >= current.endDate)
    return {
      kind: 'overnight',
      closes: current.endDate,
      reopens: next?.startDate,
    }
  }

  const overlap = (w: IMsDateInterval) =>
    Math.min(w.endDate, occurrence.endDate) -
    Math.max(w.startDate, occurrence.startDate)
  // Reaching into two of the day's windows means running through the gap
  // between them.
  if (concrete.filter(w => overlap(w) > 0).length > 1) return { kind: 'break' }

  const window = concrete.reduce((best, w) =>
    overlap(w) > overlap(best) ? w : best,
  )
  if (overlap(window) <= 0) {
    return {
      kind: 'closedThen',
      opens: window.startDate,
      closes: window.endDate,
    }
  }
  const isEarly = occurrence.startDate < window.startDate
  const isLate = occurrence.endDate > window.endDate

  if (isEarly && isLate) {
    return {
      kind: 'outsideBothEnds',
      opens: window.startDate,
      closes: window.endDate,
    }
  }
  if (isEarly) {
    return {
      kind: 'startsEarly',
      start: occurrence.startDate,
      opens: window.startDate,
    }
  }
  if (isLate) {
    return { kind: 'endsLate', end: occurrence.endDate, closes: window.endDate }
  }
  return null
}

// What is wrong with the booking, in a few words that follow the tag:
// "Outside hours · Opens at 07:00 AM".
function summarizeProblem(
  projectDateStore: ProjectDateStore,
  problem: HoursProblem,
): string {
  const time = (date: number) => projectDateStore.getTimeToDisplay(date)
  const {
    closedOnWeekdays,
    closedOnX,
    onlyOpenXToY,
    opensAtX,
    closesAtX,
    closesAtXReopensAtY,
    hasBreakDuringThisBooking,
  } = Localization.translator

  switch (problem.kind) {
    case 'closedDay':
      return problem.isWeekly
        ? closedOnWeekdays(
            projectDateStore.format(
              problem.day,
              'eeee',
              Localization.dateLocale,
            ),
          )
        : closedOnX(
            projectDateStore.getWeekdayMonthAndDayToDisplay(problem.day),
          )
    case 'closedThen':
    case 'outsideBothEnds':
      return onlyOpenXToY(time(problem.opens), time(problem.closes))
    case 'startsEarly':
      return opensAtX(time(problem.opens))
    case 'endsLate':
      return closesAtX(time(problem.closes))
    case 'overnight':
      return problem.reopens
        ? closesAtXReopensAtY(time(problem.closes), time(problem.reopens))
        : closesAtX(time(problem.closes))
    case 'break':
      return hasBreakDuringThisBooking
  }
}

function summarizeClosure(
  projectDateStore: ProjectDateStore,
  { startDate, endDate, isDaily }: IClosureSpan,
): string {
  const { startOfDay, isSameDay } = projectDateStore
  const time = (date: number) => projectDateStore.getTimeToDisplay(date)
  const day = (date: number) =>
    projectDateStore.getWeekdayMonthAndDayToDisplay(date)

  if (isDaily) {
    return Localization.translator.xToYEveryDay(time(startDate), time(endDate))
  }

  const isWholeDays =
    endDate > startDate &&
    startOfDay(startDate).getTime() === startDate &&
    startOfDay(endDate).getTime() === endDate
  if (isWholeDays) {
    const lastDay = startOfDay(endDate - 1).getTime()
    return isSameDay(startDate, lastDay)
      ? Localization.translator.allDayX(day(startDate))
      : `${day(startDate)} – ${day(lastDay)}`
  }

  return isSameDay(startDate, endDate)
    ? `${day(startDate)}, ${time(startDate)} – ${time(endDate)}`
    : `${day(startDate)}, ${time(startDate)} – ${day(endDate)}, ${time(
        endDate,
      )}`
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

function describeAmount(amount: number, unit: WorkflowDurationUnit): string {
  const {
    xMinutesDuration,
    xHoursDuration,
    xDaysDuration,
    xWeeksDuration,
    xMonthsDuration,
  } = Localization.translator

  switch (unit) {
    case WorkflowDurationUnit.Minutes:
      return xMinutesDuration(amount)
    case WorkflowDurationUnit.Hours:
      return xHoursDuration(amount)
    case WorkflowDurationUnit.Days:
      return xDaysDuration(amount)
    case WorkflowDurationUnit.Weeks:
      return xWeeksDuration(amount)
    case WorkflowDurationUnit.Months:
      return xMonthsDuration(amount)
  }
}

function describeLength(ms: number): string {
  const parts = [
    [Math.floor(ms / DAY_MS), WorkflowDurationUnit.Days],
    [Math.floor((ms % DAY_MS) / HOUR_MS), WorkflowDurationUnit.Hours],
    [Math.floor((ms % HOUR_MS) / MINUTE_MS), WorkflowDurationUnit.Minutes],
  ] as const
  return parts
    .filter(([amount]) => amount > 0)
    .slice(0, 2)
    .map(([amount, unit]) => describeAmount(amount, unit))
    .join(' ')
}

const describeLimitLength = ({
  duration,
  unit,
}: IExceededMaxDuration['limit']) => describeAmount(duration, unit)

function bookingWhen(
  projectDateStore: ProjectDateStore,
  { startDate, endDate }: IConflictingBooking,
): string {
  const time = (date: number) => projectDateStore.getTimeToDisplay(date)
  const day = (date: number) =>
    projectDateStore.getWeekdayMonthAndDayToDisplay(date)
  return projectDateStore.isSameDay(startDate, endDate)
    ? `${time(startDate)} – ${time(endDate)}`
    : `${day(startDate)}, ${time(startDate)} – ${day(endDate)}, ${time(
        endDate,
      )}`
}

const bookingName = (booking: IConflictingBooking) =>
  booking.code || Localization.translator.aBooking

export function describeOverlapRepeats(
  projectDateStore: ProjectDateStore,
  conflict: IOverlapConflict,
): string | null {
  const { failingCount, totalCount, firstFailingStart } = conflict
  if (totalCount <= 1) return null

  const first =
    projectDateStore.getWeekdayMonthAndDayToDisplay(firstFailingStart)
  return failingCount === totalCount
    ? Localization.translator.fullyBookedOnAllXRepeats(totalCount)
    : Localization.translator.fullyBookedOnXOfYRepeats(
        failingCount,
        totalCount,
        first,
      )
}

const SHOWN_CODES = 2

function overlapLine(
  projectDateStore: ProjectDateStore,
  overlap: IOverlapConflict,
): INoteLine {
  const { limit, bookings } = overlap
  const {
    fullyBooked,
    takenByX,
    takenByXAtY,
    listWithXMore,
    noRoomAtThisTime,
    takesXBookingsAtATime,
    bookingWithTime,
  } = Localization.translator
  const codes = bookings.slice(0, SHOWN_CODES).map(bookingName)
  const hidden = bookings.length - codes.length
  const text =
    bookings.length === 1
      ? takenByXAtY(codes[0], bookingWhen(projectDateStore, bookings[0]))
      : codes.length
        ? takenByX(listWithXMore(codes, hidden))
        : noRoomAtThisTime

  return {
    status: fullyBooked,
    text,
    details: [
      limit ? takesXBookingsAtATime(limit) : null,
      ...(bookings.length > 1
        ? bookings.map(booking =>
            bookingWithTime(
              bookingName(booking),
              bookingWhen(projectDateStore, booking),
            ),
          )
        : []),
      describeOverlapRepeats(projectDateStore, overlap),
    ].filter(Boolean),
  }
}

// No names: the resource is held for companies this user can't see.
function reservedForLine(companies: string[]): INoteLine {
  const { reserved, forX, listWithXMore, forOtherCompanies } =
    Localization.translator
  const shown = companies.slice(0, SHOWN_CODES)
  const hidden = companies.length - shown.length
  return {
    status: reserved,
    text: companies.length
      ? forX(listWithXMore(shown, hidden))
      : forOtherCompanies,
    details: hidden ? companies : [],
  }
}

// Whatever stops the booking — the resource being off limits, closures, booking
// limits, a full calendar — shares one note under the resource's name, a line
// each.
export function getBlockedNote(
  projectDateStore: ProjectDateStore,
  title: string,
  {
    isUnavailable,
    reservedFor,
    closures,
    maxDurations,
    overlap,
  }: {
    isUnavailable?: boolean
    // The companies it is held for; absent when it isn't reserved.
    reservedFor?: string[]
    closures: IClosureSpan[]
    maxDurations: IExceededMaxDuration[]
    overlap?: IOverlapConflict
  },
): IWarningNote {
  const {
    unavailable,
    cantBeBooked,
    closedStatus,
    listWithXMore,
    tooLong,
    limitIsX,
    thisBookingIsX,
  } = Localization.translator
  const closureTexts = Array.from(
    new Set(closures.map(c => summarizeClosure(projectDateStore, c))),
  )
  const lines: INoteLine[] = [
    ...(isUnavailable
      ? [
          {
            status: unavailable,
            text: cantBeBooked,
            details: [],
          },
        ]
      : []),
    ...(reservedFor ? [reservedForLine(reservedFor)] : []),
    ...(closureTexts.length
      ? [
          {
            status: closedStatus,
            text: listWithXMore(
              closureTexts.slice(0, 1),
              closureTexts.length - 1,
            ),
            details: closureTexts.length > 1 ? closureTexts : [],
          },
        ]
      : []),
    ...maxDurations.map(({ limit, bookingMs }) => ({
      status: tooLong,
      text: limitIsX(describeLimitLength(limit)),
      details: [thisBookingIsX(describeLength(bookingMs))],
    })),
    ...(overlap ? [overlapLine(projectDateStore, overlap)] : []),
  ]

  return { tone: 'blocked', title, lines }
}

export function describeOccurrences(
  projectDateStore: ProjectDateStore,
  warning: IHoursOfOperationWarning,
): string | null {
  const { failingCount, totalCount, firstFailingStart } = warning
  if (totalCount <= 1) return null

  const first =
    projectDateStore.getWeekdayMonthAndDayToDisplay(firstFailingStart)
  return failingCount === totalCount
    ? Localization.translator.affectsAllXRepeats(totalCount)
    : Localization.translator.affectsXOfYRepeats(
        failingCount,
        totalCount,
        first,
      )
}

function describeSchedule(warning: IHoursOfOperationWarning): string[] {
  const { projectHoursHeading, usesProjectHoursHeading, hoursHeading } =
    Localization.translator
  const heading = warning.isProject
    ? projectHoursHeading
    : warning.isProjectHours
      ? usesProjectHoursHeading
      : hoursHeading
  return [heading, ...warning.hoursLabels]
}

export function getHoursNote(
  projectDateStore: ProjectDateStore,
  warning: IHoursOfOperationWarning,
): IWarningNote {
  return {
    tone: 'warning',
    title: warning.isProject
      ? Localization.translator.project
      : warning.resourceName,
    lines: [
      {
        status: Localization.translator.outsideHours,
        text: summarizeProblem(projectDateStore, warning.problem),
        details: [
          ...describeSchedule(warning),
          describeOccurrences(projectDateStore, warning),
        ].filter(Boolean),
      },
    ],
  }
}

// The workflow type's own length limit, for the date field.
export function getTooLongNote(limit: string, bookingMs: number): IWarningNote {
  return {
    tone: 'blocked',
    lines: [
      {
        status: Localization.translator.tooLong,
        text: limit,
        details: [
          Localization.translator.thisBookingIsX(describeLength(bookingMs)),
        ],
      },
    ],
  }
}

// deadline: how far ahead it should have been asked for ("48 hours"); absent
// once the workflow exists.
export function getLateNote(
  deadline: string | null,
  isBlocked: boolean,
): IWarningNote {
  return {
    tone: isBlocked ? 'blocked' : 'warning',
    lines: [
      {
        status: Localization.translator.late,
        text: deadline
          ? Localization.translator.notRequestedXAhead(deadline)
          : Localization.translator.notRequestedInAdvance,
        details: [],
      },
    ],
  }
}
